/**
 * This component will show the booking info and calculated total price.
 * I.e. dates and other details related to payment decision in receipt format.
 */
import React from 'react';
import classNames from 'classnames';

import { FormattedMessage, useIntl } from '../../util/reactIntl';
import {
  DATE_TYPE_DATE,
  DATE_TYPE_DATETIME,
  DATE_TYPE_TIME,
  LINE_ITEM_CUSTOMER_COMMISSION,
  LINE_ITEM_PROVIDER_COMMISSION,
  LISTING_UNIT_TYPES,
  propTypes,
} from '../../util/types';
import { types as sdkTypes } from '../../util/sdkLoader';
import LineItemBookingPeriod from './LineItemBookingPeriod';
import LineItemBasePriceMaybe from './LineItemBasePriceMaybe';
import LineItemSubTotalMaybe from './LineItemSubTotalMaybe';
import LineItemShippingFeeMaybe from './LineItemShippingFeeMaybe';
import LineItemPickupFeeMaybe from './LineItemPickupFeeMaybe';
import LineItemCustomerCommissionMaybe from './LineItemCustomerCommissionMaybe';
import LineItemCustomerCommissionRefundMaybe from './LineItemCustomerCommissionRefundMaybe';
import LineItemProviderCommissionMaybe from './LineItemProviderCommissionMaybe';
import LineItemProviderCommissionRefundMaybe from './LineItemProviderCommissionRefundMaybe';
import LineItemRefundMaybe from './LineItemRefundMaybe';
import LineItemTotalPrice from './LineItemTotalPrice';
import LineItemUnknownItemsMaybe from './LineItemUnknownItemsMaybe';

import css from './OrderBreakdown.module.css';
import LineItemOmiseFeeMaybe from './LineItemCustomerOmiseFeeMaybe';
import LineItemCustomerOmiseFeeRefundMayBe from './LineItemCustomerOmiseFeeRefundMayBe';
import { CHARGE_STATUS, DURATION_TYPE, PAYMENT_METHODS, TRANSITIONS } from '../../util/enums';
import { H6 } from '../Heading/Heading';
import { getEffectiveLocale, roundPrice } from '../../util/genericHelpers';
import { useSelector } from 'react-redux';
import Icons from '../Icons/Icons';
import { formatMoney } from '../../util/currency';
import { getEffectivePaymentMethod } from '../../transactions/transaction';

const { Money } = sdkTypes;

const LINE_ITEM_SECURITY_DEPOSIT = 'line-item/security-deposit';
const LINE_ITEM_CUSTOM_OFFER = 'line-item/custom-offer';

const parseNumber = value => {
  if (value === null || value === undefined || value === '') {
    return null;
  }

  const numericValue = typeof value === 'number' ? value : parseFloat(value);
  return Number.isFinite(numericValue) ? numericValue : null;
};

const firstPositiveNumber = (...values) => values.map(parseNumber).find(value => value > 0) || null;

const hasEnabledFlag = (...values) => values.some(value => value === true || value === 'true');

const hasAnyDepositFlag = (...values) =>
  values.some(value => value === true || value === false || value === 'true' || value === 'false');

const getMoney = (value, fallbackCurrency) => {
  if (!value) {
    return null;
  }

  const rawAmount = typeof value === 'object' ? value.amount : value;
  const numericAmount = parseNumber(rawAmount);
  const amount = numericAmount ? Math.abs(numericAmount) : null;
  const moneyCurrency =
    typeof value === 'object' ? value.currency || fallbackCurrency : fallbackCurrency;
  return amount > 0 && moneyCurrency ? new Money(Math.round(amount), moneyCurrency) : null;
};

export const OrderBreakdownComponent = props => {
  const {
    rootClassName,
    className,
    userRole,
    transaction,
    booking,
    dateType,
    timeZone,
    currency,
    marketplaceName,
    intl,
    durationType,
    overTime,
    paymentOption,
    listing,
    isCheckoutPage,
    isListingPage,
    setIsDepositInstructions,
  } = props;

  const { currentUser } = useSelector(state => state.user) || {};
  const isCustomer = userRole === 'customer';
  const isProvider = userRole === 'provider';
  const allLineItems = transaction.attributes.lineItems || [];
  const lastTransition = transaction?.attributes?.lastTransition;
  const paymentMethod = getEffectivePaymentMethod(transaction) || paymentOption;
  const { refundObj, omiseChargeStatus, payinTotalAmount, payoutTotalAmount } =
    transaction?.attributes?.metadata || {};
  // We'll show only line-items that are specific for the current userRole (customer vs provider)
  const lineItems = allLineItems.filter(lineItem => lineItem.includeFor.includes(userRole));
  const unitLineItem = lineItems.find(
    item => LISTING_UNIT_TYPES.includes(item.code) && !item.reversal
  );
  const customOfferLineItem = lineItems.find(
    item => item.code === 'line-item/custom-offer' && !item.reversal
  );
  const isCustomOfferBreakdown = !!customOfferLineItem;
  // Line-item code that matches with base unit: day, night, hour, item
  const lineItemUnitType = unitLineItem?.code;

  const hasCommissionLineItem = lineItems.find(item => {
    const hasCustomerCommission = isCustomer && item.code === LINE_ITEM_CUSTOMER_COMMISSION;
    const hasProviderCommission = isProvider && item.code === LINE_ITEM_PROVIDER_COMMISSION;
    return (hasCustomerCommission || hasProviderCommission) && !item.reversal;
  });

  const selectedPaymentMethod = paymentOption || getEffectivePaymentMethod(transaction);
  const classes = classNames(rootClassName || css.root, className);

  /**
   * OrderBreakdown contains different line items:
   *
   * LineItemBookingPeriod: prints booking start and booking end types. Prop dateType
   * determines if the date and time or only the date is shown
   *
   * LineItemShippingFeeMaybe: prints the shipping fee (combining additional fee of
   * additional items into it).
   *
   * LineItemShippingFeeRefundMaybe: prints the amount of refunded shipping fee
   *
   * LineItemBasePriceMaybe: prints the base price calculation for the listing, e.g.
   * "$150.00 * 2 nights $300"
   *
   * LineItemUnknownItemsMaybe: prints the line items that are unknown. In ideal case there
   * should not be unknown line items. If you are using custom pricing, you should create
   * new custom line items if you need them.
   *
   * LineItemSubTotalMaybe: prints subtotal of line items before possible
   * commission or refunds
   *
   * LineItemRefundMaybe: prints the amount of refund
   *
   * LineItemCustomerCommissionMaybe: prints the amount of customer commission
   * The default transaction process used by this template doesn't include the customer commission.
   *
   * LineItemCustomerCommissionRefundMaybe: prints the amount of refunded customer commission
   *
   * LineItemProviderCommissionMaybe: prints the amount of provider commission
   *
   * LineItemProviderCommissionRefundMaybe: prints the amount of refunded provider commission
   *
   * LineItemTotalPrice: prints total price of the transaction
   *
   */
  const isQuantityOne = durationType === 'halfDay' || durationType === 'fullDay';
  const isCustomRefund =
    omiseChargeStatus === CHARGE_STATUS.NO_REFUND ||
    omiseChargeStatus === CHARGE_STATUS.HALF_REFUND;

  const isCancelTranistion =
    lastTransition === TRANSITIONS.CANCEL_BY_CUSTOMER ||
    lastTransition === TRANSITIONS.CANCEL_BY_PROVIDER ||
    lastTransition === TRANSITIONS.CANCEL_FROM_DISPUTED;

  const isCardDecline =
    (lastTransition === TRANSITIONS.DECLINE || lastTransition === TRANSITIONS.OPERATOR_DECLINE) &&
    paymentMethod === PAYMENT_METHODS.CARD;

  const cancelTransitions = [
    TRANSITIONS.INITIATE_REFUND,
    TRANSITIONS.CANCEL_BY_CUSTOMER,
    TRANSITIONS.CANCEL_BY_PROVIDER,
    TRANSITIONS.REQUEST_ACCOUNT_INFO_BY_PROVIDER,
    TRANSITIONS.CANCEL,
  ];
  const declineTransitions = [
    TRANSITIONS.DECLINE,
    TRANSITIONS.OPERATOR_DECLINE,
    TRANSITIONS.DECLINE_PAY_LATER,
    TRANSITIONS.OPERATOR_DECLINE_PAY_LATER,
  ];

  const isCancelledBookings = transaction?.attributes?.transitions?.some(t =>
    cancelTransitions.includes(t.transition)
  );
  const isDeclinedBookings = transaction?.attributes?.transitions?.some(t =>
    declineTransitions.includes(t.transition)
  );
  const locale = getEffectiveLocale(currentUser);
  const listingPublicData = listing?.attributes?.publicData || {};
  const txProtectedData = transaction?.attributes?.protectedData || {};
  const txMetadata = transaction?.attributes?.metadata || {};

  // Detail pages need transaction fallbacks because listing data can be unavailable or changed.
  const depositPercentageNumber = firstPositiveNumber(
    txProtectedData.depositPercentage,
    txProtectedData.securityDepositPercentage,
    txMetadata.depositPercentage,
    txMetadata.securityDepositPercentage,
    listingPublicData.depositPercentage
  );
  const fixedDepositAmount = firstPositiveNumber(
    txProtectedData.depositFixedAmount,
    txProtectedData.securityDepositFixedAmount,
    txMetadata.depositFixedAmount,
    txMetadata.securityDepositFixedAmount,
    listingPublicData.depositFixedAmount,
    listingPublicData.advancePricingDetails?.securityDeposit?.fixedAmount
  );
  const depositTypeValues = [
    txProtectedData.depositType,
    txProtectedData.securityDepositType,
    txMetadata.depositType,
    txMetadata.securityDepositType,
    listingPublicData.depositType,
    listingPublicData.advancePricingDetails?.securityDeposit?.type,
  ];
  const usesFixedDeposit = depositTypeValues.includes('fixed');
  const depositFlagValues = [
    txProtectedData.isDeposit,
    txMetadata.isDeposit,
    listingPublicData.isDeposit,
  ];
  const depositLineItem = allLineItems.find(
    item => item.code === LINE_ITEM_SECURITY_DEPOSIT && !item.reversal
  );
  const explicitDepositMoney =
    getMoney(depositLineItem?.lineTotal, currency) ||
    getMoney(txProtectedData.securityDepositAmount, currency) ||
    getMoney(txProtectedData.depositAmount, currency) ||
    getMoney(txMetadata.securityDepositAmount, currency) ||
    getMoney(txMetadata.depositAmount, currency);

  const depositBaseLineItem =
    allLineItems.find(item => item.code === LINE_ITEM_CUSTOM_OFFER && !item.reversal) ||
    customOfferLineItem ||
    allLineItems.find(item => LISTING_UNIT_TYPES.includes(item.code) && !item.reversal) ||
    unitLineItem;
  const protectedCustomOfferTotal = getMoney(txProtectedData.customOffer?.totalPrice, currency);
  const baseAmount = Math.abs(
    depositBaseLineItem?.lineTotal?.amount || protectedCustomOfferTotal?.amount || 0
  );
  const depositCurrency =
    explicitDepositMoney?.currency ||
    depositBaseLineItem?.lineTotal?.currency ||
    protectedCustomOfferTotal?.currency ||
    currency;
  const calculatedDepositAmount =
    baseAmount > 0 && depositPercentageNumber > 0
      ? Math.round((baseAmount * depositPercentageNumber) / 100)
      : 0;
  const fixedDepositAmountInSubunits =
    fixedDepositAmount > 0 ? Math.round(fixedDepositAmount * 100) : 0;
  const calculatedDepositMoney =
    depositCurrency &&
    ((usesFixedDeposit && fixedDepositAmountInSubunits > 0) ||
      (!usesFixedDeposit && calculatedDepositAmount > 0))
      ? new Money(
          usesFixedDeposit ? fixedDepositAmountInSubunits : calculatedDepositAmount,
          depositCurrency
        )
      : null;
  const depositMoney = explicitDepositMoney || calculatedDepositMoney;
  const formattedDepositPrice = depositMoney?.amount > 0 ? formatMoney(intl, depositMoney) : null;
  const depositEnabled =
    !!explicitDepositMoney ||
    hasEnabledFlag(...depositFlagValues) ||
    (!hasAnyDepositFlag(...depositFlagValues) &&
      (depositPercentageNumber > 0 || fixedDepositAmountInSubunits > 0));
  const showSecurityDeposit = depositEnabled && !!formattedDepositPrice;
  const openDepositInstructions = () => setIsDepositInstructions?.(true);

  return (
    <div className={classes}>
      <LineItemBookingPeriod
        booking={booking}
        code={lineItemUnitType}
        dateType={dateType}
        timeZone={timeZone}
        durationType={durationType}
        transaction={transaction}
        overTime={overTime}
        isCheckoutPage={isCheckoutPage}
        listing={listing}
        isListingPage={isListingPage}
        locale={locale}
      />
      <H6 as="h3" className={css.orderBreakdownTitle}>
        <FormattedMessage id={`TransactionPanel.orderBreakdownTitle`} />
      </H6>
      {isQuantityOne || isCustomOfferBreakdown ? null : (
        <LineItemBasePriceMaybe
          durationType={durationType}
          lineItems={lineItems}
          code={lineItemUnitType}
          intl={intl}
          isDeclinedBookings={isDeclinedBookings}
        />
      )}
      <LineItemShippingFeeMaybe lineItems={lineItems} intl={intl} />
      <LineItemPickupFeeMaybe lineItems={lineItems} intl={intl} />
      <LineItemUnknownItemsMaybe lineItems={lineItems} isProvider={isProvider} intl={intl} />

      {durationType === DURATION_TYPE.HOURLY || isCustomOfferBreakdown ? null : (
        <LineItemSubTotalMaybe
          lineItems={lineItems}
          code={lineItemUnitType}
          userRole={userRole}
          intl={intl}
          marketplaceCurrency={currency}
          isDeclinedBookings={isDeclinedBookings}
        />
      )}
      {isDeclinedBookings ? null : (
        <>
          <LineItemCustomerCommissionMaybe
            lineItems={lineItems}
            isCustomer={isCustomer}
            marketplaceName={marketplaceName}
            intl={intl}
          />

          {isCardDecline ? (
            <LineItemCustomerCommissionRefundMaybe
              lineItems={lineItems}
              isCustomer={isCustomer}
              marketplaceName={marketplaceName}
              intl={intl}
            />
          ) : null}

          <LineItemOmiseFeeMaybe
            lineItems={lineItems}
            isCustomer={isCustomer}
            marketplaceName={marketplaceName}
            intl={intl}
            selectedPaymentMethod={selectedPaymentMethod}
            transaction={transaction}
          />

          {isCardDecline ? (
            <LineItemCustomerOmiseFeeRefundMayBe
              lineItems={lineItems}
              isCustomer={isCustomer}
              marketplaceName={marketplaceName}
              intl={intl}
            />
          ) : null}

          {isCancelledBookings ? null : (
            <LineItemProviderCommissionMaybe
              lineItems={lineItems}
              isProvider={isProvider}
              marketplaceName={marketplaceName}
              intl={intl}
            />
          )}

          {/* {omiseChargeStatus == CHARGE_STATUS.NO_REFUND ? null : (
            <LineItemProviderCommissionRefundMaybe
              lineItems={lineItems}
              isProvider={isProvider}
              marketplaceName={marketplaceName}
              intl={intl}
              omiseChargeStatus={omiseChargeStatus}
              marketplaceCurrency={currency}
            />
          )} */}
          {omiseChargeStatus === CHARGE_STATUS.NO_REFUND ? null : (
            <LineItemRefundMaybe
              omiseChargeStatus={omiseChargeStatus}
              lineItems={lineItems}
              intl={intl}
              marketplaceCurrency={currency}
            />
          )}
        </>
      )}

      {
        <LineItemTotalPrice
          overTime={overTime}
          lineItems={lineItems}
          transaction={transaction}
          allLineItems={allLineItems}
          isProvider={isProvider}
          intl={intl}
          omiseChargeStatus={omiseChargeStatus}
          payinTotalAmount={payinTotalAmount * 100}
          payoutTotalAmount={payoutTotalAmount}
          marketplaceCurrency={currency}
          isCustomRefund={isCustomRefund}
          isCancelTranistion={isCancelTranistion}
          isCardDecline={isCardDecline}
        />
      }

      {hasCommissionLineItem ? (
        <span className={css.feeInfo}>
          <FormattedMessage id="OrderBreakdown.commissionFeeNote" />
        </span>
      ) : null}

      {showSecurityDeposit ? (
        <div className={css.securityDeposit}>
          <div className={css.securityDepositIcon}>
            <Icons name="moneyHand" />
          </div>

          <div className={css.securityDepositInfo}>
            <h2>
              <FormattedMessage
                id="OrderBreakdown.securityDepositTitle"
                values={{ depositPercentage: roundPrice(formattedDepositPrice) }}
              />
            </h2>
            <p>
              {intl.formatMessage(
                {
                  id: 'OrderBreakdown.securityDepositInfo',
                },
                {
                  instructions: (
                    <strong className={css.instructionsLink}>
                      <span
                        className={css.instructionsLink}
                        onClick={openDepositInstructions}
                        onKeyDown={event => {
                          if (event.key === 'Enter' || event.key === ' ') {
                            openDepositInstructions();
                          }
                        }}
                        role="button"
                        tabIndex="0"
                      >
                        <FormattedMessage id="StripePaymentForm.instructions" />
                      </span>
                    </strong>
                  ),
                }
              )}
            </p>
          </div>
        </div>
      ) : null}
    </div>
  );
};

/**
 * Order breakdown aka receipt
 *
 * @component
 * @param {Object} props
 * @param {string?} props.className add more style rules in addition to component's own css.root
 * @param {string?} props.rootClassName overwrite components own css.root
 * @param {string} props.marketplaceName
 * @param {string} props.timeZone IANA time zone name
 * @param {string} props.currency E.g. 'USD'
 * @param {'customer' | 'provider'} props.userRole
 * @param {propTypes.transaction} props.transaction
 * @param {propTypes.booking?} props.booking
 * @param {DATE_TYPE_DATE | DATE_TYPE_TIME | DATE_TYPE_DATETIME} props.dateType
 * @returns {JSX.Element} the order breakdown component
 */
const OrderBreakdown = props => {
  const intl = useIntl();
  return <OrderBreakdownComponent intl={intl} {...props} />;
};

export default OrderBreakdown;
